import { ScopeLevel } from "@transfr-inc/dashboard-components/outcome-metrics";

import { XAPI_EVENT_TYPES, XAPI_OBJECT_TYPES } from "./event-types";
import { xAPIEvent } from "./xapi-event.base";

/**
 * Outcomes dashboard xAPI events.
 *
 * One class per row of the outcomes-dashboard tab of the xAPI event dictionary.
 * Each carries its dictionary extensions flat on the event options; keys whose
 * value is undefined are dropped so a statement only records the signals the
 * call site actually has.
 *
 * Kept in a sibling module and re-exported from `xapi-events.js` so call sites
 * still import from the one barrel while the outcomes block stays separable.
 *
 * @module models/tracking/xapi-events.outcomes
 */

/**
 * The dictionary's registered `current_drill_level` / `selected_drill_level`
 * vocabulary, keyed by {@link ScopeLevel}. The two are deliberately separate:
 * `ScopeLevel` is a UI enum the views are free to rename.
 */
const DRILL_LEVEL_BY_SCOPE = {
  [ScopeLevel.MultiOrg]: "parent_organization",
  [ScopeLevel.Org]: "organizations",
  [ScopeLevel.Classroom]: "classroom",
  [ScopeLevel.Learner]: "learner",
};

/**
 * @param {string} [scopeLevel] - A {@link ScopeLevel} value.
 * @returns {string|undefined} Undefined for an absent or unrecognized level, so
 *   the extension key drops rather than reporting an unregistered value.
 */
function drillLevel(scopeLevel) {
  return scopeLevel ? DRILL_LEVEL_BY_SCOPE[scopeLevel] : undefined;
}

/**
 * Drops keys whose value is undefined. The survivors ride flat on the payload,
 * where the xAPI service matches each property to its extension IRI by name.
 *
 * @param {Object} extensions - Candidate object extensions (camelCase keys; the
 *   tracking service snake-cases them on send).
 * @returns {Object}
 */
function definedExtensions(extensions) {
  return Object.fromEntries(
    Object.entries(extensions).filter(([, value]) => value !== undefined)
  );
}

/**
 * User selects the arrow on a breakdown row to drill into a child level
 * (organization → classroom → learner).
 */
export class OutcomesDrillDownSelectedEvent extends xAPIEvent {
  constructor(
    scopeId,
    scopeName,
    { currentDrillLevel, selectedDrillLevel, hasChartTileRendered } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(scopeId),
        name: scopeName,
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      definedExtensions({
        currentDrillLevel: drillLevel(currentDrillLevel),
        selectedDrillLevel: drillLevel(selectedDrillLevel),
        hasChartTileRendered,
      })
    );
  }
}

/**
 * User selects a bar in a breakdown chart to drill into a child level for a
 * specific product.
 */
export class OutcomesDrillDownBarSelectedEvent extends xAPIEvent {
  constructor(
    scopeId,
    scopeName,
    {
      currentDrillLevel,
      selectedDrillLevel,
      productType,
      hasChartTileRendered,
    } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(scopeId),
        name: scopeName,
        type: XAPI_OBJECT_TYPES.BAR_CHART,
      },
      definedExtensions({
        currentDrillLevel: drillLevel(currentDrillLevel),
        selectedDrillLevel: drillLevel(selectedDrillLevel),
        productType,
        hasChartTileRendered,
      })
    );
  }
}

/**
 * User selects a breadcrumb to navigate back up the hierarchy.
 */
export class OutcomesBreadcrumbSelectedEvent extends xAPIEvent {
  constructor(
    objectId,
    { currentDrillLevel, selectedDrillLevel, hasChartTileRendered } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(objectId),
        name: "breadcrumb",
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      definedExtensions({
        currentDrillLevel: drillLevel(currentDrillLevel),
        selectedDrillLevel: drillLevel(selectedDrillLevel),
        hasChartTileRendered,
      })
    );
  }
}

/**
 * User selects the dashboard home button.
 */
export class OutcomesHomeSelectedEvent extends xAPIEvent {
  constructor(objectId) {
    super(XAPI_EVENT_TYPES.SELECTED, {
      id: String(objectId),
      name: "dashboard home button",
      type: XAPI_OBJECT_TYPES.BUTTON,
    });
  }
}

/**
 * User selects a metric button in the dashboard header.
 */
export class OutcomesMetricSelectedEvent extends xAPIEvent {
  constructor(objectId, metricName, { hasChartTileRendered } = {}) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(objectId),
        name: metricName,
        type: XAPI_OBJECT_TYPES.DASHBOARD_METRIC_BUTTON,
      },
      definedExtensions({ hasChartTileRendered })
    );
  }
}

/**
 * User selects or deselects a checkbox in the metric-visibility picker.
 */
export class OutcomesMetricVisibilityToggledEvent extends xAPIEvent {
  constructor(
    objectId,
    metricName,
    { currentState, hasChartTileRendered } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.TOGGLED,
      {
        id: String(objectId),
        name: metricName,
        type: XAPI_OBJECT_TYPES.CHECKBOX,
      },
      definedExtensions({
        dashboardMetricName: metricName,
        currentState,
        hasChartTileRendered,
      })
    );
  }
}

/**
 * User selects a product filter tab within the metric-visibility picker.
 */
export class OutcomesMetricVisibilityTabSelectedEvent extends xAPIEvent {
  constructor(objectId, tabLabel, { tabId, hasChartTileRendered } = {}) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(objectId),
        name: tabLabel,
        type: XAPI_OBJECT_TYPES.TAB,
      },
      definedExtensions({ tabId, hasChartTileRendered })
    );
  }
}

/**
 * User selects a product filter on the breakdown chart.
 */
export class OutcomesProductFilteredEvent extends xAPIEvent {
  constructor(
    objectId,
    objectName,
    { productType, hasChartTileRendered } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.FILTERED,
      {
        id: String(objectId),
        name: objectName,
        type: XAPI_OBJECT_TYPES.DASHBOARD_PRODUCT,
      },
      definedExtensions({ productType, hasChartTileRendered })
    );
  }
}

/**
 * User filters the dashboard to a non-default date range.
 */
export class OutcomesDateRangeFilteredEvent extends xAPIEvent {
  constructor(
    objectId,
    objectName,
    { startDate, endDate, presetFilters, hasChartTileRendered } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.FILTERED,
      {
        id: String(objectId),
        name: objectName,
        type: XAPI_OBJECT_TYPES.DASHBOARD_DATE,
      },
      definedExtensions({
        startDate,
        endDate,
        presetFilters,
        hasChartTileRendered,
      })
    );
  }
}

/**
 * User selects 'Done' in the organization-visibility settings.
 */
export class OutcomesOrgVisibilityDoneSelectedEvent extends xAPIEvent {
  constructor(
    objectId,
    objectName,
    {
      entitiesSelected,
      entitiesDeselected,
      currentDrillLevel,
      hasChartTileRendered,
    } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(objectId),
        name: objectName,
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      definedExtensions({
        entitiesSelected,
        entitiesDeselected,
        currentDrillLevel: drillLevel(currentDrillLevel),
        hasChartTileRendered,
      })
    );
  }
}

/**
 * User searches the org/classroom/learner lists.
 */
export class OutcomesSearchEvent extends xAPIEvent {
  constructor(
    searchBarId,
    searchBarName,
    { currentDrillLevel, hasReturnedSearchResults } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.SEARCHED,
      {
        id: String(searchBarId),
        name: searchBarName,
        type: XAPI_OBJECT_TYPES.SEARCH_BAR,
      },
      definedExtensions({
        currentDrillLevel: drillLevel(currentDrillLevel),
        hasReturnedSearchResults,
      })
    );
  }
}

/**
 * User selects the 'Impact Report' button in the 'Generate Report' pop-up.
 */
export class OutcomesImpactReportSelectedEvent extends xAPIEvent {
  constructor(objectId, { hasReportGeneratedSuccessfully } = {}) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(objectId),
        name: "Impact Report",
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      definedExtensions({ hasReportGeneratedSuccessfully })
    );
  }
}

/**
 * User selects the download button for an impact report.
 */
export class OutcomesImpactReportDownloadSelectedEvent extends xAPIEvent {
  constructor(objectId, { hasReportGeneratedSuccessfully } = {}) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(objectId),
        name: "Download",
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      definedExtensions({ hasReportGeneratedSuccessfully })
    );
  }
}

/**
 * Impact report download is received.
 */
export class OutcomesImpactReportDownloadedEvent extends xAPIEvent {
  constructor(
    objectId,
    { hasDataDownloadedSuccessfully, hasReportGeneratedSuccessfully } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.DOWNLOADED,
      {
        id: String(objectId),
        name: "Impact Report",
        type: XAPI_OBJECT_TYPES.DOWNLOAD,
      },
      definedExtensions({
        hasDataDownloadedSuccessfully,
        hasReportGeneratedSuccessfully,
      })
    );
  }
}

/**
 * User selects the raw-data (CSV) download button.
 */
export class OutcomesRawDataDownloadSelectedEvent extends xAPIEvent {
  constructor(objectId, { rawDataGranularity } = {}) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(objectId),
        name: "Download",
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      definedExtensions({ rawDataGranularity })
    );
  }
}

/**
 * Raw-data (CSV) download is received.
 */
export class OutcomesRawDataDownloadedEvent extends xAPIEvent {
  constructor(
    objectId,
    { rawDataGranularity, hasDataDownloadedSuccessfully } = {}
  ) {
    super(
      XAPI_EVENT_TYPES.DOWNLOADED,
      {
        id: String(objectId),
        name: "Raw Outcomes Data",
        type: XAPI_OBJECT_TYPES.DOWNLOAD,
      },
      definedExtensions({
        rawDataGranularity,
        hasDataDownloadedSuccessfully,
      })
    );
  }
}
